/**
 * Place詳細シート（共通コンポーネント）
 *
 * PlaceまたはVisit+Placeの詳細情報を表示
 * - 画像ギャラリー
 * - 住所、カテゴリ、ジャンル
 * - Google Maps・ウェブサイトリンク
 * - 訪問統計
 * - [Visit指定時のみ] 訪問時間編集・訪問履歴
 */

import '@splidejs/react-splide/css'

import { Splide, SplideSlide } from '@splidejs/react-splide'
import { format } from 'date-fns'
import { Camera, Clock, ExternalLink, Globe, Pencil, ReceiptText, RefreshCw, Tag } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getImmichAssetsByPlace, triggerImmichDeltaSync, type VisitPhotoGroup } from '@/api/services/immich'
import { getOverlandVisit, updateVisit } from '@/api/services/overland'
import type { OverlandVisit, Place } from '@/api/types/overland'
import { VisitHistoryCollapsible } from '@/components/shared/visit-history-collapsible'
import { AuthImage } from '@/components/ui/auth-image'
import { Badge } from '@/components/ui/badge'
import type { BottomSheetProps } from '@/components/ui/bottom-sheet'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { parseUtcDate } from '@/lib/date-utils'
import { formatSecondsJa } from '@/lib/time-format'
import { stripQueryParams } from '@/lib/utils'
import { PlaceImageManager } from '@/pages/activity/components/place-image-manager'
import { useOverlandStore } from '@/store/useOverlandStore'

import { PhotoGrid } from './activity-card/photo-grid'

/** 初期表示枚数: この Visit (到着〜出発の写真) の先頭からこの枚数までだけ見せる */
const INITIAL_PHOTO_LIMIT = 5

/**
 * 単一 Visit グループの写真セクション (表示のみ)
 * - 表示枚数のクリップは親側で行い、`visiblePhotos` として渡す
 * - ヘッダーには実際の総枚数 (`group.photos.length`) を表示
 */
const PhotoGroupSection = ({
  group,
  visiblePhotos,
  isCurrentVisit,
}: {
  group: VisitPhotoGroup
  visiblePhotos: VisitPhotoGroup['photos']
  isCurrentVisit: boolean
}) => {
  const arrival = group.arrival_date ? parseUtcDate(group.arrival_date) : null
  const departure = group.departure_date ? parseUtcDate(group.departure_date) : null
  const label = arrival
    ? `${format(arrival, 'yyyy/MM/dd HH:mm')}${departure ? ` 〜 ${format(departure, 'HH:mm')}` : ''}`
    : '時刻不明'

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <p className="text-xs text-muted-foreground">{label}</p>
        {isCurrentVisit && (
          <Badge variant="secondary" className="h-4 text-[10px] px-1.5">
            この訪問
          </Badge>
        )}
        <span className="text-muted-foreground text-xs ml-auto">{group.photos.length}枚</span>
      </div>
      <PhotoGrid assets={visiblePhotos} />
    </div>
  )
}

type Props = Omit<BottomSheetProps, 'title' | 'children'> & {
  place: Place
  visit?: OverlandVisit // Visitが指定された場合は訪問時間編集・履歴を表示
  onEditClick: () => void
  onUpdated?: () => void | Promise<void>
  /** この訪問での買い物を、レシートなしで手で登録する（Visit指定時のみ） */
  onAddReceiptClick?: () => void
}

export type PlaceSheetContentProps = {
  place: Place
  visit?: OverlandVisit
  onEditClick: () => void
  onUpdated?: () => void | Promise<void>
  /** VisitHistory等からのシート閉じ操作用 */
  onClose?: () => void
  /** この訪問での買い物を、レシートなしで手で登録する（Visit指定時のみ） */
  onAddReceiptClick?: () => void
}

export const PlaceSheetContent = ({
  place,
  visit,
  onEditClick,
  onUpdated,
  onClose,
  onAddReceiptClick,
}: PlaceSheetContentProps) => {
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false)
  const [isEditingArrival, setIsEditingArrival] = useState(false)
  const [arrivalInput, setArrivalInput] = useState('')
  const [isEditingDeparture, setIsEditingDeparture] = useState(false)
  const [departureInput, setDepartureInput] = useState('')
  const { isSubmitting: isSaving, run } = useSubmitLock()
  // 継続中訪問の滞在時間計算用: シートを開いた時点の時刻で固定
  const [nowAtOpen] = useState(() => Date.now())
  const mainSplideRef = useRef<Splide>(null)
  const thumbSplideRef = useRef<Splide>(null)
  const upsertVisit = useOverlandStore((s) => s.upsertVisit)

  // Immich 写真 (この Place の全 Visit 分)
  const [photoGroups, setPhotoGroups] = useState<VisitPhotoGroup[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)
  const [photosDisabled, setPhotosDisabled] = useState(false)
  const [photosRefreshing, setPhotosRefreshing] = useState(false)
  // 全写真を展開するか (初期はこの Visit の先頭 5 枚のみ)
  const [showAllPhotos, setShowAllPhotos] = useState(false)

  const loadPhotos = useCallback(async () => {
    setPhotosLoading(true)
    try {
      const res = await getImmichAssetsByPlace(place.id)
      setPhotoGroups(res.groups)
      setPhotosDisabled(false)
    } catch (e: unknown) {
      // 503 (Immich 未設定) は静かに非表示、他のエラーはトースト
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 503) {
        setPhotosDisabled(true)
      } else {
        toast.error('写真の取得に失敗しました')
      }
    } finally {
      setPhotosLoading(false)
    }
  }, [place.id])

  useEffect(() => {
    void loadPhotos()
  }, [loadPhotos])

  // Place / Visit が切り替わったら「さらに読み込む」展開状態をリセット
  // (places-map/index.tsx 等、同じ PlaceSheetContent インスタンスを prop 差し替えで使い回すケースがある)
  useEffect(() => {
    setShowAllPhotos(false)
  }, [place.id, visit?.id])

  const handleRefreshPhotos = useCallback(async () => {
    setPhotosRefreshing(true)
    try {
      await triggerImmichDeltaSync()
      await loadPhotos()
    } catch {
      toast.error('写真の再同期に失敗しました')
    } finally {
      setPhotosRefreshing(false)
    }
  }, [loadPhotos])

  useEffect(() => {
    if (mainSplideRef.current?.splide && thumbSplideRef.current?.splide) {
      mainSplideRef.current.splide.sync(thumbSplideRef.current.splide)
    }
  }, [place.id])

  // 到着時刻編集の開始
  const handleStartEditArrival = useCallback(() => {
    if (!visit) return
    if (visit.arrival_date) {
      const date = new Date(visit.arrival_date)
      setArrivalInput(format(date, "yyyy-MM-dd'T'HH:mm"))
    } else {
      setArrivalInput(format(new Date(), "yyyy-MM-dd'T'HH:mm"))
    }
    setIsEditingArrival(true)
  }, [visit])

  // 到着時刻を保存
  const handleSaveArrival = useCallback(async () => {
    if (!visit) return
    await run(async () => {
      try {
        const arrivalDate = new Date(arrivalInput)

        // バリデーション: 到着時刻は出発時刻より前である必要がある
        if (visit.departure_date) {
          const departureDate = new Date(visit.departure_date)
          if (arrivalDate >= departureDate) {
            toast.error('到着時刻は出発時刻より前にしてください')
            return
          }
        }

        await updateVisit(visit.id, { arrival_date: arrivalDate.toISOString() })
        toast.success('到着時刻を更新しました')
        setIsEditingArrival(false)
        // ローカル更新: 既存 visit に新しい arrival_date を反映して store に upsert
        upsertVisit({ ...visit, arrival_date: arrivalDate.toISOString() })
        await onUpdated?.()
      } catch {
        toast.error('更新に失敗しました')
      }
    })
  }, [visit, arrivalInput, onUpdated, upsertVisit, run])

  // 出発時刻編集の開始
  const handleStartEditDeparture = useCallback(() => {
    if (!visit) return
    // 既存の値があればセット、なければ到着時刻を初期値に
    if (visit.departure_date) {
      const date = new Date(visit.departure_date)
      setDepartureInput(format(date, "yyyy-MM-dd'T'HH:mm"))
    } else if (visit.arrival_date) {
      setDepartureInput(format(new Date(visit.arrival_date), "yyyy-MM-dd'T'HH:mm"))
    } else {
      setDepartureInput(format(new Date(), "yyyy-MM-dd'T'HH:mm"))
    }
    setIsEditingDeparture(true)
  }, [visit])

  // 出発時刻を保存
  const handleSaveDeparture = useCallback(async () => {
    if (!visit) return
    await run(async () => {
      try {
        const departureDate = new Date(departureInput)

        // バリデーション: 出発時刻は到着時刻より後である必要がある
        if (visit.arrival_date) {
          const arrivalDate = new Date(visit.arrival_date)
          if (departureDate <= arrivalDate) {
            toast.error('出発時刻は到着時刻より後にしてください')
            return
          }
        }

        await updateVisit(visit.id, { departure_date: departureDate.toISOString() })
        toast.success('出発時刻を更新しました')
        setIsEditingDeparture(false)
        upsertVisit({ ...visit, departure_date: departureDate.toISOString() })
        await onUpdated?.()
      } catch {
        toast.error('更新に失敗しました')
      }
    })
  }, [visit, departureInput, onUpdated, upsertVisit, run])

  // 滞在中に戻す
  const handleClearDeparture = useCallback(async () => {
    if (!visit) return
    await run(async () => {
      try {
        await updateVisit(visit.id, { departure_date: null })
        toast.success('滞在中に戻しました')
        setIsEditingDeparture(false)
        upsertVisit({ ...visit, departure_date: null })
        await onUpdated?.()
      } catch {
        toast.error('更新に失敗しました')
      }
    })
  }, [visit, onUpdated, upsertVisit, run])

  const hasImages = place.images && place.images.length > 0

  return (
    <div className="px-4 pb-4 space-y-4">
      {/* 画像ギャラリー (Splide連携) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <Camera className="size-4" />
            写真 ({place.images?.length || 0}枚)
          </h3>
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setIsImageManagerOpen(true)}>
            <Pencil className="size-3 mr-1" />
            編集
          </Button>
        </div>

        {hasImages ? (
          <div className="space-y-2">
            {/* メインスライダー */}
            <Splide
              ref={mainSplideRef}
              options={{
                type: 'loop',
                perPage: 1,
                pagination: false,
                arrows: place.images.length > 1,
                gap: '1rem',
              }}
              className="rounded-lg overflow-hidden"
            >
              {place.images.map((img) => (
                <SplideSlide key={img.id}>
                  <AuthImage
                    src={stripQueryParams(img.url)}
                    alt={place.display_name}
                    className="w-full aspect-video object-cover rounded-lg bg-muted"
                  />
                </SplideSlide>
              ))}
            </Splide>

            {/* サムネイルナビゲーション */}
            {place.images.length > 1 && (
              <Splide
                ref={thumbSplideRef}
                options={{
                  fixedWidth: 80,
                  fixedHeight: 50,
                  gap: 8,
                  rewind: true,
                  pagination: false,
                  isNavigation: true,
                  focus: 'center',
                  arrows: false,
                }}
                className="mt-2"
                aria-label="サムネイル"
              >
                {place.images.map((img) => (
                  <SplideSlide
                    key={img.id}
                    className="opacity-40 [&.is-active]:opacity-100 transition-opacity cursor-pointer border-2 border-transparent [&.is-active]:border-primary rounded-md overflow-hidden"
                  >
                    <AuthImage src={stripQueryParams(img.url)} alt="" className="w-full h-full object-cover" />
                  </SplideSlide>
                ))}
              </Splide>
            )}
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center p-6 border border-dashed rounded-lg bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors"
            onClick={() => setIsImageManagerOpen(true)}
          >
            <Camera className="size-8 text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground text-center">
              写真がありません
              <br />
              <span className="text-xs opacity-70">タップして追加</span>
            </p>
          </div>
        )}
      </div>

      <PlaceImageManager
        placeId={place.id}
        images={place.images}
        open={isImageManagerOpen}
        onOpenChange={setIsImageManagerOpen}
        onUpdated={async () => {
          // Visit に紐付くPlaceの画像が変わったら、最新 visit を取得して store に upsert
          if (visit) {
            try {
              const fresh = await getOverlandVisit(visit.id)
              upsertVisit(fresh)
            } catch {
              // 失敗時は静かに無視
            }
          }
          await onUpdated?.()
        }}
      />

      {/* カテゴリ・ジャンル */}
      {(place.categories.length > 0 || place.genres.length > 0) && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <Tag className="size-4" />
            カテゴリ
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {place.genres.map((genre) => (
              <Badge key={genre.code} variant="secondary">
                {genre.name}
              </Badge>
            ))}
            {place.genres.length === 0 &&
              place.categories.map((cat) => (
                <Badge key={cat.name} variant="secondary">
                  {cat.display_name ?? cat.name}
                </Badge>
              ))}
          </div>
        </div>
      )}

      {/* リンク */}
      <div className="space-y-2">
        {place.google_maps_url && (
          <a
            href={place.google_maps_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 rounded-lg border hover:bg-accent transition-colors"
          >
            <ExternalLink className="size-5 text-blue-500" />
            <span className="text-sm">Google Mapsで開く</span>
          </a>
        )}
        {place.website_url && (
          <a
            href={place.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 rounded-lg border hover:bg-accent transition-colors"
          >
            <Globe className="size-5 text-green-500" />
            <span className="text-sm">ウェブサイトを開く</span>
          </a>
        )}
      </div>

      {/* この訪問の時間 (Visit指定時のみ) */}
      {visit && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <Clock className="size-4" />
            この訪問の時間
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-1">
                {!isEditingArrival && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-6 hover:bg-muted underline"
                    onClick={handleStartEditArrival}
                  >
                    <p className="text-muted-foreground text-xs">到着</p>
                    <Pencil className="text-muted-foreground size-3" />
                  </Button>
                )}
              </div>
              {!isEditingArrival ? (
                <p className="font-medium">
                  {visit.arrival_date ? (
                    format(new Date(visit.arrival_date), 'M/d HH:mm')
                  ) : (
                    <span className="text-muted-foreground">不明</span>
                  )}
                </p>
              ) : (
                <div className="space-y-2">
                  <Input
                    type="datetime-local"
                    value={arrivalInput}
                    onChange={(e) => setArrivalInput(e.target.value)}
                    className="text-xs h-8"
                    disabled={isSaving}
                  />
                  <div className="flex gap-1">
                    <Button size="sm" className="h-6 text-xs flex-1" onClick={handleSaveArrival} disabled={isSaving}>
                      保存
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-xs"
                      onClick={() => setIsEditingArrival(false)}
                      disabled={isSaving}
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-1">
                {!isEditingDeparture && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-6 hover:bg-muted underline"
                    onClick={handleStartEditDeparture}
                  >
                    <p className="text-muted-foreground text-xs">出発</p>
                    <Pencil className="text-muted-foreground size-3" />
                  </Button>
                )}
              </div>
              {!isEditingDeparture ? (
                <p className="font-medium">
                  {visit.departure_date ? (
                    format(new Date(visit.departure_date), 'M/d HH:mm')
                  ) : (
                    <span className="text-green-600">滞在中</span>
                  )}
                </p>
              ) : (
                <div className="space-y-2">
                  <Input
                    type="datetime-local"
                    value={departureInput}
                    onChange={(e) => setDepartureInput(e.target.value)}
                    className="text-xs h-8"
                    disabled={isSaving}
                  />
                  <div className="flex gap-1">
                    <Button size="sm" className="h-6 text-xs flex-1" onClick={handleSaveDeparture} disabled={isSaving}>
                      保存
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-xs"
                      onClick={handleClearDeparture}
                      disabled={isSaving}
                    >
                      滞在中
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-xs"
                      onClick={() => setIsEditingDeparture(false)}
                      disabled={isSaving}
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 滞在時間 */}
          {visit.arrival_date &&
            (() => {
              const arrivalTime = new Date(visit.arrival_date).getTime()
              const departureTime = visit.departure_date ? new Date(visit.departure_date).getTime() : nowAtOpen
              const durationSec = Math.floor((departureTime - arrivalTime) / 1000)
              if (durationSec < 60) return null // 1分未満は表示しない
              return (
                <div className="p-3 rounded-lg bg-muted/50 mt-2">
                  <p className="text-muted-foreground text-xs">滞在時間</p>
                  <p className="font-medium">
                    {formatSecondsJa(durationSec)}
                    {!visit.departure_date && <span className="text-muted-foreground ml-1">(継続中)</span>}
                  </p>
                </div>
              )
            })()}

          {onAddReceiptClick && (
            <Button variant="outline" size="sm" className="mt-2 w-full gap-1.5" onClick={onAddReceiptClick}>
              <ReceiptText className="size-4" />
              レシートを手で登録
            </Button>
          )}
        </div>
      )}

      {/* Immich 写真 (この Place の全 Visit) */}
      {!photosDisabled && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium flex items-center gap-1.5">
              <Camera className="size-4 text-sky-500" />
              写真
              {photoGroups.length > 0 && (
                <span className="text-muted-foreground text-xs">
                  ({photoGroups.reduce((n, g) => n + g.photos.length, 0)}枚 / {photoGroups.length}訪問)
                </span>
              )}
            </h3>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              onClick={handleRefreshPhotos}
              disabled={photosRefreshing || photosLoading}
              aria-label="写真を再同期"
            >
              <RefreshCw className={`size-3.5 ${photosRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {photosLoading && photoGroups.length === 0 ? (
            <p className="text-xs text-muted-foreground">読み込み中…</p>
          ) : photoGroups.length === 0 ? (
            <p className="text-xs text-muted-foreground">この場所に紐付く写真はありません</p>
          ) : (
            (() => {
              // この訪問 (visit.id と一致するグループ) のインデックス。visit 未指定または一致なしなら -1
              const currentVisitIndex = visit ? photoGroups.findIndex((g) => g.visit_id === visit.id) : -1
              // 初期表示で主に見せるグループ。visit 一致グループがあればそれ、無ければ先頭グループを代替表示
              const primaryIndex = currentVisitIndex >= 0 ? currentVisitIndex : photoGroups.length > 0 ? 0 : -1
              const primaryGroup = primaryIndex >= 0 ? photoGroups.at(primaryIndex) : undefined

              const totalPhotos = photoGroups.reduce((n, g) => n + g.photos.length, 0)

              const displayGroups = showAllPhotos
                ? photoGroups.map((group, i) => ({
                    group,
                    visiblePhotos: group.photos,
                    // "この訪問" バッジは visit 指定時 & 一致グループのみに付与
                    isCurrentVisit: currentVisitIndex >= 0 && i === currentVisitIndex,
                  }))
                : primaryGroup
                  ? [
                      {
                        group: primaryGroup,
                        visiblePhotos: primaryGroup.photos.slice(0, INITIAL_PHOTO_LIMIT),
                        isCurrentVisit: currentVisitIndex >= 0,
                      },
                    ]
                  : []

              const shownPhotos = displayGroups.reduce((n, x) => n + x.visiblePhotos.length, 0)
              const remainingPhotos = totalPhotos - shownPhotos

              return (
                <div className="space-y-3">
                  {displayGroups.map(({ group, visiblePhotos, isCurrentVisit }) => (
                    <PhotoGroupSection
                      key={group.visit_id}
                      group={group}
                      visiblePhotos={visiblePhotos}
                      isCurrentVisit={isCurrentVisit}
                    />
                  ))}
                  {remainingPhotos > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full h-8 text-xs"
                      onClick={() => setShowAllPhotos(true)}
                    >
                      さらに読み込む (残り {remainingPhotos} 枚)
                    </Button>
                  )}
                </div>
              )
            })()
          )}
        </div>
      )}

      {/* 訪問統計 */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">訪問情報</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-muted-foreground text-xs">訪問回数</p>
            <p className="font-medium">{place.visit_count}回</p>
          </div>
          {place.last_visited_at && (
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-muted-foreground text-xs">最終訪問</p>
              <p className="font-medium">
                {new Date(place.last_visited_at).toLocaleDateString('ja-JP', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
            </div>
          )}
        </div>

        {/* 訪問日リスト */}
        {place.visit_count > 0 && (
          <VisitHistoryCollapsible placeId={place.id} currentVisitId={visit?.id} onClose={() => onClose?.()} />
        )}
      </div>

      {/* 座標 */}
      <div className="space-y-1">
        <h3 className="text-sm font-medium">座標</h3>
        <p className="text-xs text-muted-foreground font-mono">
          {place.latitude.toFixed(6)}, {place.longitude.toFixed(6)}
        </p>
      </div>

      {/* フッター */}
      <div className="pt-4 border-t">
        <Button onClick={onEditClick} variant="outline" className="w-full gap-2">
          {visit ? (
            <>編集</>
          ) : (
            <>
              <Pencil className="size-4" />
              Google で再取得
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

/** PlaceSheet——BottomSheetラッパー。既存の単独シート利用向け */
export const PlaceSheet = ({
  place,
  visit,
  open,
  onOpenChange,
  onEditClick,
  onUpdated,
  onAddReceiptClick,
  ...sheetProps
}: Props) => (
  <BottomSheet
    title={place.display_name}
    open={open}
    onOpenChange={onOpenChange}
    snapPoints={[1, 0.85]}
    defaultSnapPoint={0.85}
    {...sheetProps}
  >
    <PlaceSheetContent
      place={place}
      visit={visit}
      onEditClick={onEditClick}
      onUpdated={onUpdated}
      onClose={() => onOpenChange(false)}
      onAddReceiptClick={onAddReceiptClick}
    />
  </BottomSheet>
)
